import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Tree-cluster UI state.
 *
 * When a node has status="past", every non-traveled sibling subtree is hidden
 * behind a single "+N" cluster node in the orchestrator tree. The user can
 * click that cluster to expand it, revealing the real sibling subtrees.
 *
 * MUTEX: only one cluster is expanded at a time. Expanding a different cluster
 * collapses the previous one. There is no "collapse this cluster" button —
 * users collapse implicitly by expanding another, or by the call advancing
 * past the parent.
 *
 * `clusteringDisabled` is the user-controlled kill switch — when true, no
 * cluster placeholders are rendered at all and every non-traveled sibling
 * shows as its own raw node. Persisted to localStorage so the choice survives
 * reloads.
 */

export interface TreeClusterState {
  /**
   * The parentId whose non-traveled siblings are currently expanded.
   * null means all clusters in the tree are collapsed (default state).
   */
  expandedClusterParentId: string | null;
  /**
   * User-controlled kill switch: when true, the orchestrator tree renders
   * every non-traveled sibling raw — no "+N" cluster placeholders at all.
   * Persisted to localStorage.
   */
  clusteringDisabled: boolean;
}

const initialState: TreeClusterState = {
  expandedClusterParentId: null,
  clusteringDisabled: false,
};

const treeClusterSlice = createSlice({
  name: 'treeCluster',
  initialState,
  reducers: {
    /**
     * Expand the cluster under `parentId`, collapsing any previously
     * expanded cluster (mutex by construction). Toggling the same parentId
     * again is a no-op via the explicit `releaseCluster` reducer below;
     * callers can also call this with the current id and the slice will
     * just re-set it (no-op effectively).
     */
    expandCluster: (state, action: PayloadAction<{ parentId: string }>) => {
      state.expandedClusterParentId = action.payload.parentId;
    },

    /**
     * Collapse the active cluster. Used by:
     * - the click-toggle handler (when user clicks the same cluster twice)
     * - render-time auto-release when the parent disappears or no longer
     *   has 2+ non-traveled siblings.
     */
    releaseCluster: (state) => {
      state.expandedClusterParentId = null;
    },

    /**
     * Toggle the kill switch. Also releases
     * any expanded cluster on the way through (clusters won't render at all
     * when disabled, so leaving stale expanded state would be confusing).
     */
    setClusteringDisabled: (state, action: PayloadAction<boolean>) => {
      state.clusteringDisabled = action.payload;
      if (action.payload) state.expandedClusterParentId = null;
    },
  },
});

export const { expandCluster, releaseCluster, setClusteringDisabled } =
  treeClusterSlice.actions;

export default treeClusterSlice.reducer;

// ── Constants ────────────────────────────────────────────────────────────────

/**
 * Minimum number of non-traveled siblings to form a cluster. Below this,
 * siblings render normally (clustering one thing is silly).
 */
export const TREE_CLUSTER_MIN_SIBLINGS = 2;
